'use client';

import { Slot } from '@radix-ui/react-slot';
import {
  use,
  useId,
  createContext,
  type JSX,
  type ComponentProps,
} from 'react';
import {
  Controller,
  FormProvider,
  useFormState,
  useFormContext,
  type FieldPath,
  type FieldError,
  type FieldValues,
  type SubmitHandler,
  type ControllerProps,
  type FormProviderProps,
} from 'react-hook-form';

import { cn } from '~/utils';
import { Label } from '~/components/label';

export interface IFormProps<T extends FieldValues = FieldValues>
  extends FormProviderProps<T> {
  id?: string;
  className?: string;
  onSubmit: SubmitHandler<T>;
}

export function Form<T extends FieldValues = FieldValues>({
  id,
  onSubmit,
  children,
  className,
  ...rest
}: IFormProps<T>): JSX.Element {
  const { handleSubmit } = rest;

  return (
    <FormProvider {...rest}>
      <form
        id={id}
        onSubmit={handleSubmit(onSubmit)}
        className={cn('flex flex-col gap-4', className)}
      >
        {children}
      </form>
    </FormProvider>
  );
}

interface IFormFieldContextValueProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

const FormFieldContext = createContext<IFormFieldContextValueProps>(
  {} as IFormFieldContextValueProps,
);

export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, ...props }: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext.Provider value={{ name }}>
      <Controller name={name} {...props} />
    </FormFieldContext.Provider>
  );
}

export function useFormField() {
  const itemContext = use(FormItemContext);
  const fieldContext = use(FormFieldContext);
  const { getFieldState } = useFormContext();

  const formState = useFormState({
    name: fieldContext.name,
  });

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formMessageId: `${id}-form-item-message`,
    formDescriptionId: `${id}-form-item-description`,
    ...fieldState,
  };
}

interface IFormItemContextValueProps {
  id: string;
}

const FormItemContext = createContext<IFormItemContextValueProps>(
  {} as IFormItemContextValueProps,
);

export function FormItem({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  const id = useId();

  return (
    <FormItemContext.Provider
      value={{
        id,
      }}
    >
      <div
        data-slot='form-item'
        className={cn('grid gap-2', className)}
        {...props}
      />
    </FormItemContext.Provider>
  );
}

interface IFormLabelProps extends ComponentProps<typeof Label> {
  required?: boolean;
}

export function FormLabel({
  className,
  required = false,
  ...props
}: IFormLabelProps): JSX.Element {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-error={!!error}
      htmlFor={formItemId}
      data-slot='form-label'
      className={cn(
        'data-[error=true]:text-destructive gap-0',
        required &&
          "after:text-desire-500 after:font-medium after:text-(length:--text-sm) after:content-['*']",
        className,
      )}
      {...props}
    />
  );
}

export function FormControl(props: ComponentProps<typeof Slot>): JSX.Element {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      id={formItemId}
      aria-invalid={!!error}
      data-slot='form-control'
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      {...props}
    />
  );
}

export function FormDescription({
  className,
  ...props
}: ComponentProps<'p'>): JSX.Element {
  const { formDescriptionId } = useFormField();

  return (
    <p
      id={formDescriptionId}
      data-slot='form-description'
      className={cn('text-dimGray-500 text-sm leading-[130%]', className)}
      {...props}
    />
  );
}

export function FormMessage({
  className,
  ...props
}: ComponentProps<'p'>): JSX.Element | null {
  const { error, formMessageId } = useFormField();
  const body = error ? String(handleError(error)) : props.children;

  if (!body) {
    return null;
  }

  return (
    <p
      id={formMessageId}
      data-slot='form-message'
      className={cn('text-destructive text-sm', className)}
      {...props}
    >
      {typeof body === 'string'
        ? Array.from(new Set(body.split('\n'))).map((value, i) => {
            return (
              <span
                key={i}
                className={cn('text-destructive text-sm block', className)}
              >
                {value}
              </span>
            );
          })
        : body}
    </p>
  );
}

function handleError(error: FieldError): string {
  if (error.message) {
    return error.message;
  }

  return new Set(
    Object.values(error as unknown as Record<string, FieldError>).map(
      (e) => e.message,
    ),
  )
    .values()
    .toArray()
    .join('\n');
}
